import React, { useState, useEffect } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { X, AlertCircle, RefreshCw } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface ErrorBannerProps {
  title?: string;
  message: string;
  onRetry?: (() => void | Promise<void>) | undefined;
  onDismiss?: () => void;
  dismissible?: boolean;
  autoDismiss?: boolean;
  autoDismissDelay?: number; // in milliseconds
  className?: string;
  variant?: 'default' | 'destructive';
}

/**
 * ErrorBanner - Persistent banner for global/system errors
 *
 * Shows at top of page (below header, above main content)
 * Used for:
 * - Global system errors
 * - Data loading failures
 * - Non-critical system errors that need user visibility
 */
// eslint-disable-next-line max-lines-per-function
export const ErrorBanner: React.FC<ErrorBannerProps> = ({
  title = 'Error',
  message,
  onRetry,
  onDismiss,
  dismissible = true,
  autoDismiss = false,
  autoDismissDelay = 0,
  className,
  variant = 'destructive'
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isRetrying, setIsRetrying] = useState(false);

  // Auto-dismiss after delay if enabled
  useEffect(() => {
    if (autoDismiss && autoDismissDelay > 0 && isVisible) {
      const timer = setTimeout(() => {
        handleDismiss();
      }, autoDismissDelay);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [autoDismiss, autoDismissDelay, isVisible]);

  const handleDismiss = () => {
    setIsVisible(false);
    onDismiss?.();
  };

  const handleRetry = async () => {
    if (!onRetry) return;

    setIsRetrying(true);
    try {
      await onRetry();
      // Optionally dismiss after successful retry
      // handleDismiss();
    } catch (error) {
      // Error persists, keep banner visible
      console.error('Retry failed:', error);
    } finally {
      setIsRetrying(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Alert
      variant={variant}
      className={cn(
        'border-l-4 rounded-lg shadow-md',
        variant === 'destructive' && 'border-l-destructive',
        className
      )}
    >
      <div className='flex items-start gap-3'>
        <AlertCircle className='h-5 w-5 mt-0.5 flex-shrink-0' />
        <div className='flex-1 min-w-0'>
          <AlertTitle className='mb-1'>{title}</AlertTitle>
          <AlertDescription className='text-sm text-muted-foreground'>{message}</AlertDescription>
        </div>
        <div className='flex items-center gap-2 flex-shrink-0'>
          {onRetry && (
            <Button
              variant='outline'
              size='sm'
              onClick={handleRetry}
              disabled={isRetrying}
              className='h-8'
            >
              {isRetrying ? (
                <>
                  <RefreshCw className='h-3 w-3 mr-1 animate-spin' />
                  Retrying...
                </>
              ) : (
                <>
                  <RefreshCw className='h-3 w-3 mr-1' />
                  Retry
                </>
              )}
            </Button>
          )}
          {dismissible && (
            <Button
              variant='ghost'
              size='sm'
              onClick={handleDismiss}
              className='h-8 w-8 p-0'
              aria-label='Dismiss error'
            >
              <X className='h-4 w-4' />
            </Button>
          )}
        </div>
      </div>
    </Alert>
  );
};
